import { useCallback, useMemo, useState, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
// Filter controls + the detail drawer use the ui kit; the feed TABLE is hand-built with Tailwind.
import { Descriptions, Input, Select, DatePicker, Button, FilterResetButton, Pagination, Drawer, Segmented, StatCard, EmptyState, Tooltip } from '../../ui';
import {
  Activity, ArrowDownLeft, ArrowUpRight, BadgeCheck, Boxes, CheckCircle2, Layers, Minus,
  PackageCheck, PackagePlus, Plus, RefreshCw, Search, ShoppingCart, Truck, Undo2, XCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUrlString, useUrlNumber, useUrlDateRange } from '../../hooks/useUrlState';
import { usePermission, useCanSeeWarehouses } from '../../store/auth';
import { request } from '../../api/client';
import { formatIst, formatIstDate, formatIstTime, formatIstRelative, nowIst } from '../../lib/time';
import { fmtQty, productLabel, batchSuffix } from '../../lib/format';
import { disableFromDate, disableToDate } from '../../lib/dateRange';
import { FilterRange, useFilterPanel } from '../../components/FilterPanel';
import { RoleBadge } from '../../components/RoleBadge';
import { OrderLink, ProductLink, UserLink } from '../../components/EntityLinks';
import { MasterTag, warehouseOption, warehouseText } from '../../lib/warehouseLabel';

/* ---------------------------------------------------------------------------
 * WHAT HAPPENED IN THE WAREHOUSE — one colour-coded feed.
 *
 * The first cut of this page was a five-column table of grey text: every row looked identical, the
 * one wide "Activity" column stretched a short sentence across half the screen and left a dead strip
 * before the last two columns, and the person who acted was a bare name with no hint of their role.
 *
 * So: every event now carries its OWN hue (an icon tile + a left accent bar keyed to what happened —
 * green for stock arriving, red for a cancellation or stock leaving, gold for packing…), the row is
 * split into the columns the eye actually looks for (what moved / how it changed / against which
 * document), and the actor gets a RoleBadge beside their name like everywhere else in the app.
 * ------------------------------------------------------------------------- */

type Tone = 'green' | 'lime' | 'gold' | 'orange' | 'red' | 'sky' | 'violet' | 'slate';

/** One hue per tone, used by the icon tile, the left accent bar and the inline chip. */
const TONE: Record<Tone, { tile: string; bar: string; pill: string }> = {
  green: { tile: 'bg-brand-500/12 text-brand-700 dark:text-brand-300', bar: 'bg-brand-500', pill: 'border-brand-500/30 bg-brand-500/10 text-brand-700 dark:text-brand-300' },
  lime: { tile: 'bg-lime-500/15 text-lime-700 dark:text-lime-400', bar: 'bg-lime-500', pill: 'border-lime-500/30 bg-lime-500/12 text-lime-700 dark:text-lime-400' },
  gold: { tile: 'bg-harvest-400/20 text-harvest-700 dark:text-harvest-300', bar: 'bg-harvest-400', pill: 'border-harvest-400/40 bg-harvest-400/15 text-harvest-700 dark:text-harvest-300' },
  orange: { tile: 'bg-warning-500/15 text-warning-600 dark:text-harvest-300', bar: 'bg-warning-500', pill: 'border-warning-500/35 bg-warning-500/12 text-warning-600 dark:text-harvest-300' },
  red: { tile: 'bg-danger-500/12 text-danger-600 dark:text-danger-500', bar: 'bg-danger-500', pill: 'border-danger-500/30 bg-danger-500/10 text-danger-600 dark:text-danger-500' },
  sky: { tile: 'bg-sky-500/12 text-sky-600 dark:text-sky-400', bar: 'bg-sky-500', pill: 'border-sky-500/30 bg-sky-500/10 text-sky-600 dark:text-sky-400' },
  violet: { tile: 'bg-violet-500/12 text-violet-600 dark:text-violet-400', bar: 'bg-violet-500', pill: 'border-violet-500/30 bg-violet-500/10 text-violet-600 dark:text-violet-400' },
  slate: { tile: 'bg-surface-3 text-muted', bar: 'bg-line-strong', pill: 'border-line bg-surface-3 text-muted' },
};

type EventMeta = { label: string; Icon: typeof Truck; tone: Tone };

/** Order fulfilment events, in lifecycle order — the status dropdown reads off this map too. */
const ORDER_EVENT: Record<string, EventMeta> = {
  ACCEPTED: { label: 'In fulfillment', Icon: CheckCircle2, tone: 'violet' },
  PACKED: { label: 'Packed', Icon: PackageCheck, tone: 'gold' },
  DISPATCHED: { label: 'Dispatched', Icon: Truck, tone: 'orange' },
  DELIVERED: { label: 'Delivered', Icon: BadgeCheck, tone: 'green' },
  CANCELLED: { label: 'Cancelled', Icon: XCircle, tone: 'red' },
};

/** Stock-ledger txn types → label + hue. Direction (in / out) drives the colour. */
const STOCK_EVENT: Record<string, EventMeta> = {
  GRN: { label: 'Received (GRN)', Icon: PackagePlus, tone: 'green' },
  SO: { label: 'Sales dispatch', Icon: Truck, tone: 'orange' },
  TRANSFER_IN: { label: 'Transfer in', Icon: ArrowDownLeft, tone: 'sky' },
  TRANSFER_OUT: { label: 'Transfer out', Icon: ArrowUpRight, tone: 'violet' },
  ADJUST_IN: { label: 'Adjustment in', Icon: Plus, tone: 'lime' },
  ADJUST_OUT: { label: 'Adjustment out', Icon: Minus, tone: 'red' },
  RETURN_IN: { label: 'Return in', Icon: Undo2, tone: 'gold' },
  OPENING: { label: 'Opening stock', Icon: Layers, tone: 'slate' },
};

/** `APPROVED` / `TRANSFER_IN` → `Approved` / `Transfer in`. Used for the statuses this feed shows
 *  but does not itself emit (a move FROM `APPROVED`), so casing never jumps mid-sentence. */
const pretty = (s: string) => {
  const t = String(s ?? '').replace(/_/g, ' ').trim().toLowerCase();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
};

const FALLBACK: EventMeta = { label: 'Movement', Icon: Boxes, tone: 'slate' };
const eventOf = (r: any): EventMeta =>
  r.kind === 'ORDER'
    ? (ORDER_EVENT[r.toStatus] ?? { ...FALLBACK, Icon: ShoppingCart, label: pretty(r.toStatus) })
    : (STOCK_EVENT[r.txnType] ?? { ...FALLBACK, label: pretty(r.txnType) });

const TH = 'px-3 py-2 text-left type-overline text-subtle';
const TD = 'px-3 py-2.5 align-middle';

/** A soft tinted chip in one of the event hues. */
function Chip({ tone, children, className = '' }: { tone: Tone; children: React.ReactNode; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-pill border px-2 py-px type-label leading-5 ${TONE[tone].pill} ${className}`}>
      {children}
    </span>
  );
}

/** Unified "what happened in my warehouse" feed for the Warehouse Manager — order fulfilment events
 *  (accept/pack/dispatch/deliver/cancel) merged with stock movements (GRN, transfers, adjustments,
 *  sales). Server-scoped to the caller's assigned warehouse(s); admins see all. */
export function WarehouseActivityPage({ embedded }: { embedded?: boolean } = {}) {
  const canSeeWarehouses = useCanSeeWarehouses();
  const navigate = useNavigate();
  const canStock = usePermission('STOCK.VIEW');
  const canGrn = usePermission('GRN.VIEW');
  const canWarehouses = usePermission('WAREHOUSE.VIEW');
  const [kind, setKind] = useUrlString<'ALL' | 'ORDER' | 'STOCK'>('kind', 'ALL');
  const [search, setSearch] = useUrlString('q');
  const [typeFilter, setTypeFilter] = useUrlString<string | undefined>('type', undefined);  // order status OR stock txn type
  const [warehouseId, setWarehouseId] = useUrlString<string | undefined>('wh', undefined);  // admin only (a WM is auto-scoped)
  const [actor, setActor] = useUrlString<string | undefined>('by', undefined);              // who acted
  const [range, setRange] = useUrlDateRange('range');
  const [detail, setDetail] = useState<any>(null); // a stock movement shown in the side drawer
  const [page, setPage] = useUrlNumber('page', 1);
  const [pageSize, setPageSize] = useUrlNumber('pageSize', 25);

  // Where a movement's Reference links to — only when the viewer can actually open the target page.
  const refLink = (r: any): string | null => {
    if (r.orderId) return `/orders/${r.orderId}`;
    if (r.refTable === 'stock_transfers') return canStock ? '/inventory/master-stock?tab=transfers' : null;
    if (r.refTable === 'stock_adjustments') return canStock ? '/inventory/master-stock?tab=adjustments' : null;
    if (r.refTable === 'grn_headers') return canGrn ? '/inventory/grn' : null;
    return null;
  };
  const go = (to: string) => { setDetail(null); navigate(to); };
  // Stock delta as a soft tinted chip: light green for in, light red for out.
  const qtyDelta = (qty: number) => (
    <Chip tone={qty >= 0 ? 'green' : 'red'} className="tabular-nums">
      {qty >= 0 ? '+' : '−'}{fmtQty(Math.abs(qty))}
    </Chip>
  );

  // Date window + warehouse filter server-side (so older / other-warehouse history is fetched);
  // type, status/movement, actor and free-text search narrow the fetched window client-side.
  const from = range[0] ? range[0].startOf('day').toISOString() : undefined;
  const to = range[1] ? range[1].endOf('day').toISOString() : undefined;
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['warehouse-activity', warehouseId, from, to],
    queryFn: () => request<any[]>({ url: '/warehouse-activity', params: { warehouseId, from, to, limit: 250 } }),
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
  // Warehouse picker options — admins only; a WM is locked to their own warehouse server-side.
  const { data: warehouses } = useQuery({
    queryKey: ['warehouses', 'all'],
    queryFn: () => request<any[]>({ url: '/warehouses/my' }),
    // Gated so a role without warehouse access doesn't fire a call it is refused — see useCanSeeWarehouses.
    enabled: canSeeWarehouses && canWarehouses,
    staleTime: 5 * 60_000,
  });

  /* The status / movement dropdown is contextual to the Kind segmented — and each option wears the
     SAME chip the feed draws for that event, so what you pick in the filter is recognisable as the
     thing you then see in the rows. A plain list of five words made you translate between the two.
     `title` keeps the plain text for the tooltip and for a11y, since the label is now JSX. */
  const typeOptions = (kind === 'ORDER' ? Object.entries(ORDER_EVENT) : kind === 'STOCK' ? Object.entries(STOCK_EVENT) : [])
    .map(([value, m]) => ({
      value,
      title: m.label,
      label: <Chip tone={m.tone}><m.Icon size={11} />{m.label}</Chip>,
    }));

  /* The KPI band counts the SAME window the table reads, MINUS the two filters the tiles themselves
     set (kind + status/movement) — otherwise clicking "Order events" would drop every sibling tile
     to zero. Scope filters stay on (warehouse + dates server-side; search + actor here). */
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data ?? [])
      // `sourceTransferNo` is in the haystack so searching a transfer number finds BOTH halves of
      // the movement — the dispatch out of the source and the receipt into the destination.
      .filter((r) => !q || [r.orderNo, r.productName, r.sku, r.batchNo, r.ref, r.sourceTransferNo, r.actorName, r.warehouseCode]
        .some((x) => String(x ?? '').toLowerCase().includes(q)));
  }, [data, search]);

  const base = useMemo(
    () => searched.filter((r) => !actor || String(r.actorName ?? 'System') === actor),
    [searched, actor],
  );

  /* Kind + status/movement as ONE predicate, because two things apply it: the table, and the count
     beside each name in the actor picker. Sharing it is what keeps that count honest. */
  const matchesKind = useCallback(
    (r: any) => (kind === 'ALL' || r.kind === kind)
      && (!typeFilter || (r.kind === 'ORDER' ? r.toStatus === typeFilter : r.txnType === typeFilter)),
    [kind, typeFilter],
  );

  const rows = useMemo(() => base.filter(matchesKind), [base, matchesKind]);

  const kpi = useMemo(() => {
    const orders = base.filter((r) => r.kind === 'ORDER');
    const stock = base.filter((r) => r.kind === 'STOCK');
    return {
      all: base.length,
      orders: orders.length,
      stock: stock.length,
      stockIn: stock.filter((r) => Number(r.qty) >= 0).length,
      stockOut: stock.filter((r) => Number(r.qty) < 0).length,
      cancelled: orders.filter((r) => r.toStatus === 'CANCELLED').length,
    };
  }, [base]);

  /* Who acted, as a facet. The number beside a name is what picking that name WILL leave in the
     table — every other filter already applied — not their running total for the window. It used to
     be the total, which read as a promise the table then broke: with Type = Orders on, the picker
     offered "Super Admin 186" and the table answered with 37. The figure you click is now the
     figure you get. A name the current filters zero out drops off the list; the SELECTED name is
     kept at 0, since removing it would leave the filter applied with no way back to it. */
  const actorOptions = useMemo(() => {
    // Names and roles come off the whole window so nobody loses their badge; only the COUNT reads
    // the filtered set.
    const seen = new Map<string, { role?: string | null; n: number }>();
    for (const r of data ?? []) {
      const name = String(r.actorName ?? 'System');
      if (!seen.has(name)) seen.set(name, { role: r.actorRole, n: 0 });
    }
    if (actor && !seen.has(actor)) seen.set(actor, { role: null, n: 0 });
    for (const r of searched) {
      if (!matchesKind(r)) continue;
      const hit = seen.get(String(r.actorName ?? 'System'));
      if (hit) hit.n += 1;
    }
    return [...seen.entries()]
      .filter(([name, v]) => v.n > 0 || name === actor)
      .sort((a, b) => b[1].n - a[1].n)
      .map(([name, v]) => ({ value: name, label: name, role: v.role, n: v.n }));
  }, [data, searched, matchesKind, actor]);

  // A tile's click REPLACES the category filters it represents, so the number on the tile is exactly
  // what the table then shows — it never stacks onto whatever was already selected.
  const setKindOnly = (k: 'ALL' | 'ORDER' | 'STOCK', t?: string) => { setKind(k); setTypeFilter(t); setPage(1); };

  const chips = [
    range[0] || range[1] ? {
      key: 'when',
      label: `When: ${range[0] ? formatIstDate(range[0]) : 'any'} → ${range[1] ? formatIstDate(range[1]) : 'today'}`,
      onRemove: () => { setRange([null, null]); setPage(1); },
    } : null,
    kind !== 'ALL' ? { key: 'kind', label: `Type: ${kind === 'ORDER' ? 'Orders' : 'Stock'}`, onRemove: () => setKindOnly('ALL') } : null,
    typeFilter ? { key: 'type', label: (ORDER_EVENT[typeFilter] ?? STOCK_EVENT[typeFilter])?.label ?? typeFilter, onRemove: () => { setTypeFilter(undefined); setPage(1); } } : null,
    warehouseId ? {
      key: 'wh',
      label: `Warehouse: ${warehouseText((warehouses ?? []).find((w: any) => String(w.id) === warehouseId) ?? {})}`,
      onRemove: () => { setWarehouseId(undefined); setPage(1); },
    } : null,
    actor ? { key: 'by', label: `By: ${actor}`, onRemove: () => { setActor(undefined); setPage(1); } } : null,
  ].filter(Boolean) as { key: string; label: string; onRemove: () => void }[];

  const hasFilters = !!(search || typeFilter || warehouseId || actor || range[0] || range[1] || kind !== 'ALL');
  const reset = () => {
    setSearch(''); setTypeFilter(undefined); setWarehouseId(undefined); setActor(undefined);
    setRange([null, null]); setKind('ALL'); setPage(1);
  };

  // Six filters is past what an inline toolbar carries: they open IN PLACE under the toolbar, one
  // section per COLUMN, in the same order as the columns.
  const filters = useFilterPanel({
    onClear: reset,
    chips,
    groups: [
      {
        title: 'When',
        fields: [{
          label: 'Date range',
          span: 2,
          // Two independent pickers, NOT a RangePicker: each bound applies on its own, so
          // "everything since Monday" is a filter you can actually express.
          control: (
            <FilterRange
              from={<DatePicker value={range[0]} placeholder="From" disabledDate={disableFromDate(range[1])}
                onChange={(d) => { setRange([d, range[1]]); setPage(1); }} />}
              to={<DatePicker value={range[1]} placeholder="To" disabledDate={disableToDate(range[0])}
                onChange={(d) => { setRange([range[0], d]); setPage(1); }} />}
            />
          ),
        }],
      },
      {
        title: 'Event',
        fields: [
          {
            label: 'Kind',
            span: 2,
            control: (
              <Segmented
                value={kind}
                onChange={(v) => setKindOnly(v as any)}
                options={[{ label: 'All', value: 'ALL' }, { label: 'Orders', value: 'ORDER' }, { label: 'Stock', value: 'STOCK' }]}
              />
            ),
          },
          {
            label: kind === 'STOCK' ? 'Movement' : 'Status',
            control: (
              <Select
                allowClear disabled={kind === 'ALL'} value={typeFilter} options={typeOptions}
                placeholder={kind === 'ALL' ? 'Pick a kind first' : kind === 'ORDER' ? 'e.g. Dispatched' : 'e.g. Transfer in'}
                onChange={(v) => { setTypeFilter(v); setPage(1); }}
              />
            ),
            hint: kind === 'ALL' ? 'Choose Orders or Stock first' : undefined,
          },
        ],
      },
      ...(canWarehouses ? [{
        title: 'Warehouse',
        fields: [{
          label: 'Location',
          control: (
            <Select
              allowClear showSearch optionFilterProp="searchLabel" placeholder="All warehouses"
              value={warehouseId} onChange={(v) => { setWarehouseId(v); setPage(1); }}
              options={(warehouses ?? []).map((w: any) => ({ value: String(w.id), ...warehouseOption(w) }))}
            />
          ),
        }],
      }] : []),
      {
        title: 'By',
        fields: [{
          label: 'Who acted',
          hint: 'How many rows each name leaves, with the other filters applied',
          control: (
            <Select
              allowClear showSearch optionFilterProp="label" placeholder="Anyone"
              value={actor} onChange={(v) => { setActor(v); setPage(1); }}
              options={actorOptions}
              optionRender={(o: any) => (
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate">{o.label ?? o.data?.label}</span>
                  <RoleBadge role={o.role ?? o.data?.role} />
                  <span className="ml-auto shrink-0 type-caption text-subtle">{o.n ?? o.data?.n}</span>
                </span>
              )}
            />
          ),
        }],
      },
    ],
  });

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(page, pageCount);
  const paged = rows.slice((current - 1) * pageSize, current * pageSize);

  // A day heading breaks the feed into readable runs instead of one undifferentiated wall of rows.
  const today = nowIst().format('YYYY-MM-DD');
  const yesterday = nowIst().subtract(1, 'day').format('YYYY-MM-DD');
  const dayKey = (at: any) => formatIst(at, 'YYYY-MM-DD');
  const dayLabel = (k: string) => (k === today ? 'Today' : k === yesterday ? 'Yesterday' : formatIst(k, 'dddd'));

  return (
    <div>
      {!embedded && <h2 className="mb-3 type-section-title text-content">Warehouse activity</h2>}

      {/* ── KPI band: the shape of the window, and the fastest way to narrow it ─────────────── */}
      <div className="mb-3.5 grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard
          tone="neutral" icon={<Activity size={18} />} label="All events" value={data ? kpi.all : '—'}
          hint={range[0] || range[1] ? 'in the chosen dates' : 'latest 250, newest first'}
          onClick={kpi.all ? () => setKindOnly('ALL') : undefined} active={kind === 'ALL' && !typeFilter}
        />
        <StatCard
          tone="sky" icon={<ShoppingCart size={18} />} label="Order events" value={data ? kpi.orders : '—'}
          hint="in fulfillment → packed → dispatched → delivered"
          onClick={kpi.orders ? () => setKindOnly('ORDER') : undefined} active={kind === 'ORDER' && !typeFilter}
        />
        <StatCard
          tone="gold" icon={<Boxes size={18} />} label="Stock movements" value={data ? kpi.stock : '—'}
          hint={`${kpi.stockIn} in · ${kpi.stockOut} out`}
          onClick={kpi.stock ? () => setKindOnly('STOCK') : undefined} active={kind === 'STOCK' && !typeFilter}
        />
        <StatCard
          tone="danger" icon={<XCircle size={18} />} label="Cancellations" value={data ? kpi.cancelled : '—'}
          hint={kpi.cancelled ? 'orders stopped after approval' : 'none in this window'}
          onClick={kpi.cancelled ? () => setKindOnly('ORDER', 'CANCELLED') : undefined}
          active={kind === 'ORDER' && typeFilter === 'CANCELLED'}
        />
      </div>

      {/* ── Toolbar: search stays inline, the rest opens in place under it ──────────────────── */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input
          placeholder="e.g. GB/2026/09/00168 or GreenGro" aria-label="e.g. GB/2026/09/00168 or GreenGro"
          prefix={<Search size={16} className="text-subtle" />}
          style={{ width: 300 }}
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        {filters.trigger}
        {filters.chips}
        <span className="ml-auto flex items-center gap-2">
          <span className="type-caption text-subtle">
            {rows.length === kpi.all ? `${rows.length} event${rows.length === 1 ? '' : 's'}` : `${rows.length} of ${kpi.all} events`}
          </span>
          <Tooltip label="Refresh">
            <Button variant="text" size="sm" aria-label="Refresh" onClick={() => refetch()}
              icon={<RefreshCw size={16} className={isFetching ? 'animate-spin' : undefined} />} />
          </Tooltip>
        </span>
      </div>
      {filters.panel}

      {/* ── The feed ───────────────────────────────────────────────────────────────────────── */}
      <div className="overflow-x-auto rounded-card border border-line bg-surface shadow-e1">
        <table className="w-full min-w-[70rem] table-fixed border-collapse text-sm">
          {/* Fixed layout + explicit widths: the two free-text columns absorb the leftover width, so
              it never piles up as one dead strip before Warehouse / By. The fixed columns alone are
              51.5rem, so below ~1100px the two free columns had nothing left (0px at 768) and the order
              number printed over the warehouse — `min-w` keeps them ~9rem and the table scrolls instead. */}
          <colgroup>
            <col className="w-[8.5rem]" />
            <col className="w-[19rem]" />
            <col />
            <col />
            <col className="w-[11rem]" />
            <col className="w-[13rem]" />
          </colgroup>
          <thead className="gb-thead-tint bg-surface-2">
            <tr>
              <th className={TH}>When</th>
              <th className={TH}>What happened</th>
              <th className={TH}>Order / product</th>
              <th className={TH}>Reference</th>
              <th className={TH}>Warehouse</th>
              <th className={TH}>By</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && !rows.length ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="border-t border-line"><td colSpan={6} className="px-3 py-3"><div className="h-6 animate-pulse rounded bg-surface-3" /></td></tr>
              ))
            ) : paged.length ? (
              paged.map((r: any, i: number) => {
                const meta = eventOf(r);
                const tone = meta.tone;
                const k = dayKey(r.at);
                const newDay = i === 0 || dayKey(paged[i - 1].at) !== k;
                const clickable = r.kind === 'ORDER' ? !!r.orderId : true;
                return (
                  <Fragment key={r.id}>
                    {newDay && (
                      <tr className="border-t border-line">
                        <td colSpan={6} className="bg-surface-2/70 px-3 py-1.5">
                          <span className="type-overline text-subtle">{dayLabel(k)}</span>
                          <span className="ml-2 type-caption text-subtle">{formatIst(r.at, 'DD MMM YYYY')}</span>
                        </td>
                      </tr>
                    )}
                    <tr
                      className={`border-t border-line transition-colors hover:bg-surface-2/70 ${clickable ? 'cursor-pointer' : ''}`}
                      onClick={() => { if (r.kind === 'ORDER') { if (r.orderId) navigate(`/orders/${r.orderId}`); } else setDetail(r); }}
                    >
                      {/* When — the accent bar rides the first cell, so the row's colour is readable
                          before any of its text is. */}
                      <td className={`${TD} relative pl-4`}>
                        <span className={`absolute inset-y-0 left-0 w-1 ${TONE[tone].bar}`} aria-hidden />
                        <div className="whitespace-nowrap type-label-strong text-content">{formatIstTime(r.at)}</div>
                        <div className="whitespace-nowrap type-caption text-subtle" title={formatIst(r.at)}>{formatIstRelative(r.at)}</div>
                      </td>

                      {/* What happened — icon tile + the change itself, in one cell. An earlier cut
                          split the event name and the from→to move across two columns, so half the
                          rows printed "Cancelled … Cancelled" and burned a column doing it. */}
                      <td className={TD}>
                        <span className="flex min-w-0 items-center gap-2">
                          <span className={`grid size-7 shrink-0 place-items-center rounded-control ${TONE[tone].tile}`}>
                            <meta.Icon size={15} />
                          </span>
                          {r.kind === 'ORDER' ? (
                            <span className="flex min-w-0 flex-wrap items-center gap-1">
                              {r.fromStatus && (
                                <>
                                  <span className="type-label text-muted">{ORDER_EVENT[r.fromStatus]?.label ?? pretty(r.fromStatus)}</span>
                                  <span className="text-subtle">→</span>
                                </>
                              )}
                              <Chip tone={tone}>{meta.label}</Chip>
                            </span>
                          ) : (
                            <span className="flex min-w-0 items-center gap-2">
                              <span className="min-w-0 truncate type-label-strong text-content" title={meta.label}>{meta.label}</span>
                              {qtyDelta(r.qty)}
                            </span>
                          )}
                        </span>
                      </td>

                      {/* Order / product — the subject of the event, linked where a page exists. */}
                      <td className={TD}>
                        {r.kind === 'ORDER' ? (
                          <OrderLink id={r.orderId} className="type-label-strong">{r.orderNo}</OrderLink>
                        ) : (
/* One product, said once. The name, its SKU and its batch number are each minted from
                             the one before ("AGN" / "AGN 500ml" / "AGN 500ml-OPENING-377"), so printed
                             raw this cell said "AGN" three times. productLabel keeps whichever of name
                             and SKU is the whole truth; batchSuffix drops the product off the batch and
                             the title keeps the full number. */
                          (() => {
                            const { primary, secondary } = productLabel(r.productName, r.sku);
                            const batch = batchSuffix(r.batchNo, r.sku, r.productName, primary);
                            const sub = [secondary, batch].filter(Boolean).join(' · ');
                            return (
                              <div className="min-w-0">
                                <div className="truncate text-content" title={primary || undefined}>
                                  <ProductLink name={r.productName}>{primary || '—'}</ProductLink>
                                </div>
                                {sub && (
                                  <div className="truncate type-caption text-subtle" title={r.batchNo ?? undefined}>{sub}</div>
                                )}
                              </div>
                            );
                          })()
                        )}
                      </td>

                      {/* Reference — the note behind an order move, or the document a movement cites. */}
                      <td className={TD}>
                        {r.kind === 'ORDER'
                          ? (r.note
                            ? <span className="line-clamp-2 text-muted" title={r.note}>{r.note}</span>
                            : <span className="text-subtle">—</span>)
                          : (r.ref
                            ? (
                              <div className="min-w-0">
                                {refLink(r)
                                  ? <button type="button" title={r.ref}
                                    className="block w-full truncate text-left text-brand-700 underline-offset-2 hover:underline dark:text-brand-400"
                                    onClick={(e) => { e.stopPropagation(); navigate(refLink(r)!); }}>{r.ref}</button>
                                  : <span className="block truncate text-muted" title={r.ref}>{r.ref}</span>}
                                {/* A transfer's ARRIVAL is a GRN, not a TRANSFER_IN — receiving one
                                    materialises a goods receipt at the destination and THAT posts the
                                    inward. So a "Received (GRN)" row sitting beside a "Transfer out" row
                                    IS the other half of one movement, and gave no sign of it. Naming the
                                    transfer here closes the pair. */}
                                {r.sourceTransferNo && (
                                  <button type="button"
                                    title={`The arriving half of transfer ${r.sourceTransferNo}${r.sourceWarehouseName ? ` from ${r.sourceWarehouseName}` : ''}`}
                                    className="mt-0.5 block w-full truncate text-left type-caption text-subtle underline-offset-2 hover:text-brand-700 hover:underline dark:hover:text-brand-400"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (canStock) navigate(`/inventory/master-stock?tab=transfers&focus=${r.sourceTransferId}`);
                                    }}>
                                    ← {r.sourceTransferNo}{r.sourceWarehouseCode ? ` · from ${r.sourceWarehouseCode}` : ''}
                                  </button>
                                )}
                              </div>
                            )
                            : <span className="text-subtle">—</span>)}
                      </td>

                      {/* Warehouse — code first (that is what people say out loud), master capsule kept. */}
                      <td className={TD}>
                        {(r.warehouseCode || r.warehouseName) ? (
                          <div className="min-w-0">
                            <div className="flex min-w-0 items-center">
                              <span className="truncate type-label-strong text-content">{r.warehouseCode ?? r.warehouseName}</span>
                              {r.whIsMaster ? <MasterTag /> : null}
                            </div>
                            {r.warehouseCode && r.warehouseName && (
                              <div className="truncate type-caption text-subtle" title={r.warehouseName}>{r.warehouseName}</div>
                            )}
                          </div>
                        ) : <span className="text-subtle">—</span>}
                      </td>

                      {/* By — name AND role, the same coloured pill used everywhere else in the app. */}
                      <td className={TD}>
                        {r.actorName ? (
                          <div className="min-w-0">
                            <div className="truncate text-content" title={r.actorName}>
                              <UserLink id={r.actorId} role={r.actorRole}>{r.actorName}</UserLink>
                            </div>
                            <RoleBadge role={r.actorRole} className="mt-0.5" />
                          </div>
                        ) : <span className="type-caption text-subtle">System</span>}
                      </td>
                    </tr>
                  </Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={6}>
                  <EmptyState
                    icon={<Activity />}
                    title={hasFilters ? 'No event matches these filters' : 'No activity yet'}
                    description={hasFilters
                      ? 'Nothing in this window matches every filter you have applied. Clear them to see the whole feed.'
                      : 'Order moves and stock movements appear here as they happen.'}
                    action={hasFilters ? <FilterResetButton onClick={reset} /> : undefined}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {rows.length > 0 && (
        <div className="mt-3 flex items-center justify-end">
          <Pagination
            size="small" current={current} pageSize={pageSize} total={rows.length}
            showSizeChanger onChange={(p, ps) => { setPage(p); setPageSize(ps); }}
            showTotal={(t) => `${t} events`}
          />
        </div>
      )}

      <Drawer open={!!detail} onClose={() => setDetail(null)} width={440} title="Stock movement">
        {detail && (
          <>
            <div className="mb-4 flex items-center gap-3 rounded-card border border-line bg-surface-2/50 p-3">
              <span className={`grid size-10 shrink-0 place-items-center rounded-control ${TONE[eventOf(detail).tone].tile}`}>
                {(() => { const I = eventOf(detail).Icon; return <I size={20} />; })()}
              </span>
              <div className="min-w-0">
                <div className="type-body-strong text-content">{eventOf(detail).label}</div>
                <div className="type-caption text-subtle">{formatIst(detail.at)} · {formatIstRelative(detail.at)}</div>
              </div>
              <span className="ml-auto">{qtyDelta(detail.qty)}</span>
            </div>
            <Descriptions column={1} size="small" colon>
              <Descriptions.Item label="Product">{productLabel(detail.productName, detail.sku).primary || '—'}</Descriptions.Item>
              <Descriptions.Item label="Batch">{detail.batchNo ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Reference">
                {detail.ref
                  ? (refLink(detail) ? <a onClick={() => go(refLink(detail)!)}>{detail.ref}</a> : detail.ref)
                  : '—'}
              </Descriptions.Item>
              {/* Only on a GRN that is a transfer's arriving half — a purchase GRN has no transfer
                  behind it, so the row is simply absent rather than showing an em-dash for a
                  question that was never asked. */}
              {detail.sourceTransferNo && (
                <Descriptions.Item label="Arrived from">
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    {canStock
                      ? <a onClick={() => go(`/inventory/master-stock?tab=transfers&focus=${detail.sourceTransferId}`)}>{detail.sourceTransferNo}</a>
                      : detail.sourceTransferNo}
                    {detail.sourceWarehouseCode && <span className="text-subtle">· {detail.sourceWarehouseName ?? detail.sourceWarehouseCode}</span>}
                  </span>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Warehouse">
                <span className="inline-flex items-center">
                  {(detail.warehouseCode || detail.warehouseName)
                    ? (canWarehouses
                      ? <a onClick={() => go('/inventory/warehouses')}>{detail.warehouseCode ?? detail.warehouseName}</a>
                      : (detail.warehouseCode ?? detail.warehouseName))
                    : '—'}
                  {detail.whIsMaster ? <MasterTag /> : null}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="By">
                <span className="inline-flex items-center gap-1.5">
                  {detail.actorName
                    ? <UserLink id={detail.actorId} role={detail.actorRole}>{detail.actorName}</UserLink>
                    : 'System'}
                  <RoleBadge role={detail.actorRole} />
                </span>
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Drawer>
    </div>
  );
}
